import { Form as CoreForm } from '#layers/core/app/runtime/utils/form';
import type { ValidationSchematic, FormType } from '#layers/core/app/runtime/utils/form';

/**
 * Rozszerzona klasa Form dla warstwy intralearn
 * Dodaje metodę formatData do oryginalnej klasy Form
 */
export class Form
{
    private coreForm: FormType<any>;

    /**
     * Konstruktor tworzący formularz z metodą formatData
     */
    public constructor(data: any, validationSchema?: ValidationSchematic)
    {
        this.coreForm = CoreForm.create(data, validationSchema);
    }

    /**
     * Fetch all relevant data without key value pair for the form.
     */
    public formatData(customKeys: string[] = []): any
    {
        const keys = ['key', 'id', ...customKeys];
        const data = {} as any;

        for (const item in (this.coreForm as any).$initial)
        {
            // Pobierz wartość z this.coreForm[item] (aktualna wartość w formularzu)
            data[item] = (this.coreForm as any)[item] as any;

            const isKeyValuePair = data[item] ? Object.keys(data[item]).some(key => keys.includes(key)) : false;
            const isArray = data[item] ? Array.isArray(data[item]) : false;

            if (isKeyValuePair || isArray)
            {
                keys.forEach((key: string, index) =>
                {
                    if (isArray)
                    {
                        // Dla tablic - sprawdź czy elementy mają klucz
                        data[item] = data[item][index] && data[item][index][key]
                            ? data[item].map((arrayItem: any) => arrayItem[key])
                            : data[item];
                    }
                    else
                    {
                        // Dla obiektów - sprawdź czy ma klucz i wyciągnij wartość
                        data[item] = data[item] && (data[item][key] !== undefined)
                            ? data[item][key]
                            : data[item];
                    }
                });
            }
        }

        return data;
    }

    /**
     * Deleguje wszystkie inne metody do coreForm
     */
    public get $validators() { return (this.coreForm as any).$validators; }
    public get $loading() { return (this.coreForm as any).$loading; }
    public get $loaded() { return (this.coreForm as any).$loaded; }
    public get $changed() { return (this.coreForm as any).$changed; }
    public get $errors() { return (this.coreForm as any).$errors; }

    public withData(data: any) { return (this.coreForm as any).withData(data); }
    public withDefault(data: any) { return (this.coreForm as any).withDefault(data); }
    public withErrors(errors: any) { return (this.coreForm as any).withErrors(errors); }
    public data() { return (this.coreForm as any).data(); }
    public only(props: string[]) { return (this.coreForm as any).only(props); }
    public except(props: string[]) { return (this.coreForm as any).except(props); }
    public reset(field?: string) { return (this.coreForm as any).reset(field); }
    public clear() { return (this.coreForm as any).clear(); }
    public loading() { return (this.coreForm as any).loading(); }
    public loaded() { return (this.coreForm as any).loaded(); }
    public wait(forceLoading?: boolean) { return (this.coreForm as any).wait(forceLoading); }
    public continue() { return (this.coreForm as any).continue(); }
    public complete(status?: boolean) { return (this.coreForm as any).complete(status); }
    public dirty() { return (this.coreForm as any).dirty(); }
    public ready(values: Promise<boolean>[]) { return (this.coreForm as any).ready(values); }
    public valid() { return (this.coreForm as any).valid(); }
    public active() { return (this.coreForm as any).active(); }
    public validationSchema(rules: any) { return (this.coreForm as any).validationSchema(rules); }
    public validateFieldFromSchema(field: string) { return (this.coreForm as any).validateFieldFromSchema(field); }
    public validateFieldFromSchemaAsync(field: string) { return (this.coreForm as any).validateFieldFromSchemaAsync(field); }
    public validateFromSchema() { return (this.coreForm as any).validateFromSchema(); }
    public validateFromSchemaAsync() { return (this.coreForm as any).validateFromSchemaAsync(); }
    public validate(rules: any, throwErrors?: boolean, showModal?: boolean) { return (this.coreForm as any).validate(rules, throwErrors, showModal); }
    public handleFocusOut(event: any) { return (this.coreForm as any).handleFocusOut(event); }
    public handleFocusIn(event: any) { return (this.coreForm as any).handleFocusIn(event); }

    /**
     * Tworzy nową instancję Form z metodą formatData
     */
    public static create<T>(data: T, validationSchema?: ValidationSchematic): FormType<T> & Form
    {
        const wrapper = new Form(data, validationSchema);
        const core = wrapper.coreForm as any;

        core.formatData = function formatData(customKeys: string[] = []): any
        {
            const keys = ['key', 'id', ...customKeys];
            const result = {} as any;

            for (const item in core.$initial)
            {
                result[item] = core[item] as any;

                const isKeyValuePair = result[item] ? Object.keys(result[item]).some((k: string) => keys.includes(k)) : false;
                const isArray = result[item] ? Array.isArray(result[item]) : false;

                if (isKeyValuePair || isArray)
                {
                    keys.forEach((key: string, index) =>
                    {
                        if (isArray)
                        {
                            result[item] = result[item][index] && result[item][index][key]
                                ? result[item].map((arrayItem: any) => arrayItem[key])
                                : result[item];
                        }
                        else
                        {
                            result[item] = result[item] && (result[item][key] !== undefined)
                                ? result[item][key]
                                : result[item];
                        }
                    });
                }
            }

            return result;
        };

        return core as FormType<T> & Form;
    }
}

export type { ValidationSchematic, FormType };
